import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    FormControl,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import axios from 'axios';
import { API_BASE_URL } from '@config';
import useStyles from 'app/cluster-styles/useStyles';
import { ipValid } from '../../util/InputValidate';
import { RESOURCE_MANAGER } from '../../util/enums';
import SectionCard from './SectionCard';

const MANAGER_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'slurm', label: 'Slurm' },
    { value: 'torque', label: 'Torque' },
];

let moabIntegrationCache = null;

async function fetchMoabIntegration() {
    if (moabIntegrationCache !== null) return moabIntegrationCache;
    try {
        const response = await axios.get(`${API_BASE_URL}/api/moab/isIntegrated`);
        if (response.status !== 200) return false;
        moabIntegrationCache = response.data.isIntegrated;
        return moabIntegrationCache;
    } catch {
        return false;
    }
}

export default function ResourceManagerConfiguration({ form, setField, hideBursting, disabled }) {
    const classes = useStyles();
    const [moabIsIntegrated, setMoabIsIntegrated] = useState(null);
    const prevManagerRef = useRef(form.manager);

    const isTorque = form.manager === RESOURCE_MANAGER.TORQUE;
    const showBursting = moabIsIntegrated && !hideBursting;
    const fieldsDisabled = !isTorque || disabled;
    const ipLabel = form.serverIsPrivate ? 'Moab Private IP' : 'Moab IP';

    // Fetch moab integration status (cached at module level)
    useEffect(() => {
        let cancelled = false;
        fetchMoabIntegration().then((result) => {
            if (!cancelled) setMoabIsIntegrated(result);
        });
        return () => { cancelled = true; };
    }, []);

    // Clear moab fields when switching away from torque
    useEffect(() => {
        const prev = prevManagerRef.current;
        prevManagerRef.current = form.manager;
        if (prev === form.manager || form.manager === RESOURCE_MANAGER.TORQUE) return;
        setField({ burstingMode: 'off', moabHostname: '', moabIp: '' });
    }, [form.manager, setField]);

    return (
        <SectionCard icon="settings_applications" title="Resource Manager Configuration" fillHeight>
                <div className="flex gap-4 mb-16">
                    <TextField
                        select
                        className={clsx(classes.input, "flex-1 mr-16")}
                        id="rm-manager"
                        name="rm_manager"
                        label="Manager"
                        variant="outlined"
                        value={form.manager}
                        onChange={(e) => setField('manager', e.target.value)}
                        required
                        disabled={disabled}
                        SelectProps={{
                            MenuProps: {
                                PaperProps: {
                                    style: { maxHeight: 200 },
                                },
                            },
                        }}
                    >
                        {MANAGER_OPTIONS.map((option) => (
                            <MenuItem key={option.value} id={`rm-${option.value}`} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        className={clsx(classes.input, "flex-1 mr-16")}
                        id="rm-hostname"
                        name="rm_hostname"
                        label="Moab Hostname"
                        type="text"
                        variant="outlined"
                        value={form.moabHostname || ''}
                        fullWidth
                        required={isTorque}
                        disabled={fieldsDisabled}
                        placeholder="Moab Hostname"
                        onChange={(e) => setField('moabHostname', e.target.value)}
                        InputProps={{
                            readOnly: fieldsDisabled,
                            style: {
                                pointerEvents: fieldsDisabled ? 'none' : 'auto',
                            },
                        }}
                        inputProps={{
                            style: {
                                color: fieldsDisabled ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                            },
                        }}
                    />

                    <TextField
                        className={clsx(classes.input, "flex-1")}
                        id="rm-ip"
                        name="rm_ip"
                        label={ipLabel}
                        type="text"
                        variant="outlined"
                        value={form.moabIp || ''}
                        fullWidth
                        required={isTorque}
                        disabled={fieldsDisabled}
                        placeholder={ipLabel}
                        onChange={(e) => setField('moabIp', e.target.value)}
                        InputProps={{
                            readOnly: fieldsDisabled,
                            style: {
                                pointerEvents: fieldsDisabled ? 'none' : 'auto',
                            },
                        }}
                        inputProps={{
                            style: {
                                color: fieldsDisabled ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                            },
                        }}
                        error={Boolean(form.moabIp && !ipValid(form.moabIp))}
                        helperText={form.moabIp && !ipValid(form.moabIp) ? 'Not a valid IP' : ''}
                    />
                </div>

                {showBursting && (
                    <Box mt={2} sx={!isTorque ? { opacity: 0.6 } : {}}>
                        <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>
                            Bursting Configuration:
                        </Typography>
                        <FormControl component="fieldset" disabled={!isTorque || disabled}>
                            <RadioGroup
                                row
                                id="rm-burstOnDeploy"
                                name="burstOnDeploy"
                                value={form.burstingMode || 'off'}
                                onChange={(e) => {
                                    if (isTorque) {
                                        setField('burstingMode', e.target.value);
                                    }
                                }}
                            >
                                <FormControlLabel value="off" id="rm-radio-off" control={<Radio color="primary" />} label="Off" />
                                <FormControlLabel value="min" id="rm-radio-min" control={<Radio color="primary" />} label="Min" />
                                <FormControlLabel value="max" id="rm-radio-max" control={<Radio color="primary" />} label="Max" />
                                <FormControlLabel value="all" id="rm-radio-all" control={<Radio color="primary" />} label="All" />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                )}
        </SectionCard>
    );
}
